import { copyToClipboard } from "@ui/lib/clipboard"
import { Check, Copy } from "lucide-react"
import { useEffect, useRef, useState } from "react"

const COPY_FEEDBACK_DURATION_MS = 2000

export function InlineCopyCode({ code }: { code: string }) {
  const [copied, setCopied] = useState(false)
  const feedbackTimeoutRef = useRef<number | null>(null)

  useEffect(() => {
    return () => {
      if (feedbackTimeoutRef.current) {
        window.clearTimeout(feedbackTimeoutRef.current)
      }
    }
  }, [])

  async function handleCopy() {
    if (feedbackTimeoutRef.current) {
      window.clearTimeout(feedbackTimeoutRef.current)
    }

    const success = await copyToClipboard(code)
    if (!success) {
      return
    }

    setCopied(true)
    feedbackTimeoutRef.current = window.setTimeout(
      () => setCopied(false),
      COPY_FEEDBACK_DURATION_MS,
    )
  }

  return (
    <span className="inline">
      <button
        type="button"
        onClick={handleCopy}
        aria-label={`Copy ${code}`}
        title={copied ? "Copied" : `Copy ${code}`}
        className="inline-flex cursor-pointer items-center gap-1 rounded-lg border border-default bg-raised px-1.5 py-0.5 font-mono text-xs font-semibold whitespace-nowrap text-default before:content-none after:content-none hover:bg-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        style={{ transform: "translateY(-1px)" }}
      >
        {code}
        <span className="relative size-3 text-muted" aria-hidden="true">
          <Copy
            size={12}
            className={`absolute inset-0 transition-opacity ${copied ? "opacity-0" : "opacity-100"}`}
          />
          <Check
            size={12}
            className={`absolute inset-0 text-green-600 transition-opacity ${copied ? "opacity-100" : "opacity-0"}`}
          />
        </span>
      </button>
      <span className="sr-only" aria-live="polite">
        {copied ? `${code} copied to clipboard` : ""}
      </span>
    </span>
  )
}
